import { TransformedValues, yupResolver } from "@mantine/form";
import { showNotification } from "@mantine/notifications";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { FirestoreError } from "firebase/firestore";
import { useEffect } from "react";
import { Asserts } from "yup";
import { TConfiguracaoFormularioIdeias } from "@/modules/configuracao-formulario-ideias";
import useUserStore from "@/modules/users/store";
import { captureException } from "@/services/log";
import { useIdeiaForm as useIdeiaFormContext } from "./ideia-form-context";
import { updateIdeia, addIdeia, TIdeiaForm } from "../../firestore";
import { useCheckSimilarIdeas, SimilarIdea } from "../../hooks/useCheckSimilarIdeas";
import { getDynamicIdeiaSchema } from "../../schema/dynamic-schema";
import ideiaSchema from "../../schema/ideia-schema";
import { QUERY_KEY } from "../ideias-table/IdeiasTable";

type FormInputs = Asserts<typeof ideiaSchema>;

/**
 * Callback chamado quando ideias similares são encontradas durante o submit.
 * O componente pai deve mostrar um modal e chamar onConfirm ou onCancel.
 */
export interface SimilarIdeasCallbacks {
  similarIdeas: SimilarIdea[];
  onConfirm: () => void;
  onCancel: () => void;
}

interface UseIdeiaFormProperties {
  onSuccess?: (ideiaId: string, variables: FormInputs, context: unknown) => undefined | Promise<unknown>;
  onError?: (error: FirestoreError, variables: FormInputs, context: unknown) => undefined | Promise<unknown>;
  configuracao?: TConfiguracaoFormularioIdeias;
  /**
   * Callback chamado quando ideias similares são encontradas.
   * O componente pai deve exibir um modal para o usuário decidir.
   */
  onSimilarIdeasFound?: (callbacks: SimilarIdeasCallbacks) => void;
}

export const formValuesCache = new Map<string, any>();
export const CACHE_KEY = "PENDING_FORM_VALUES";

/**
 * Empresa Form hook
 * @param ideia - Empresa data
 * @param root0 - Options
 * @param root0.onSuccess - On success callback
 * @param root0.onError - On error callback
 * @param root0.configuracao - Configuração do formulário de ideias
 * @param root0.onSimilarIdeasFound
 * @returns - Empresa Form
 */
function useIdeiaForm(
  ideia: TIdeiaForm & Partial<Pick<TIdeia, "id" | "refPath">>,
  { onSuccess, onError, configuracao, onSimilarIdeasFound }: UseIdeiaFormProperties
) {
  const [activeEmpresaId, activeLicenciadaId, colaborador] = useUserStore((state) => [
    state.activeEmpresaId,
    state.activeLicenciadaId,
    state.colaborador
  ]);
  const queryClient = useQueryClient();
  const { mutateAsync: checkSimilarIdeas, isPending: isCheckingSimilarity } = useCheckSimilarIdeas();

  const schema = configuracao ? getDynamicIdeiaSchema(configuracao) : ideiaSchema;

  const form = useIdeiaFormContext({
    validate: yupResolver(schema),
    transformValues: (values) => schema.cast(values) as TIdeiaForm,
    initialValues: {
      ...(schema.getDefault() as TIdeiaForm),
      ...ideia,
      autorReference: (ideia.autorReference || colaborador?.refPath) ?? ""
    }
  });

  useEffect(() => {
    const cachedValues = formValuesCache.get(CACHE_KEY);
    if (cachedValues) {
      form.setValues(cachedValues);
      formValuesCache.delete(CACHE_KEY);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { mutate, isPending, reset } = useMutation<
    string | undefined, // O tipo de retorno da mutationFn: string para addIdeia, void para updateIdeia
    FirestoreError,
    FormInputs
  >({
    mutationFn: (values) => {
      if (!activeEmpresaId) {
        throw new Error("Nenhuma empresa selecionada");
      }
      return ideia.id
        ? updateIdeia(activeEmpresaId, ideia.id, values).then(() => ideia.id)
        : addIdeia(activeEmpresaId, values).then((id) => id || "");
    },
    async onSuccess(returnedData, variables, context) {
      showNotification({
        message: `${variables.titulo} ${ideia.id ? "atualizada" : "cadastrada"} com sucesso!`,
        color: "green",
        autoClose: 2500
      });
      reset();
      form.reset();
      formValuesCache.delete(CACHE_KEY);
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      if (onSuccess) {
        // Determina o ID final: se returnedData for uma string, é o novo ID; senão, é o ideia.id existente.
        // O '!' em ideia.id! é seguro aqui, pois se returnedData não for string, significa que foi uma atualização,
        // e ideia.id deve estar definido nesse cenário.
        const finalIdeiaId = typeof returnedData === "string" ? returnedData : ideia.id?.toString();
        return onSuccess(finalIdeiaId || "", variables, context);
      }
      return null;
    },
    onError(error, variables, context) {
      captureException(error, true);
      if (onError) {
        void onError(error, variables, context);
      }
    }
  });

  type TFormTransformed = TransformedValues<typeof form>;

  /**
   * Handle form submit with similarity check
   * @param values - Form values
   */
  async function handleFormSubmit(values: TFormTransformed) {
    const originalFormValues = form.getValues();
    formValuesCache.set(CACHE_KEY, originalFormValues);

    if (ideia.id) {
      mutate(values);
      return;
    }

    if (!activeEmpresaId) {
      showNotification({
        message: "Nenhuma empresa selecionada",
        color: "red"
      });
      return;
    }

    try {
      const similarIdeas = await checkSimilarIdeas({
        titulo: values.titulo,
        descricao: values.descricao || "",
        licenciadaId: activeLicenciadaId,
        empresaId: activeEmpresaId,
        minSimilarity: 0.7,
        maxResults: 5
      });

      if (!similarIdeas || similarIdeas.length === 0) {
        mutate(values);
        return;
      }

      if (onSimilarIdeasFound) {
        onSimilarIdeasFound({
          similarIdeas,
          onConfirm: () => {
            mutate(values);
          },
          onCancel: () => {
            form.setValues(originalFormValues);
          }
        });
      } else {
        mutate(values);
      }
    } catch (error) {
      captureException(error as Error, true);
      showNotification({
        title: "Erro na verificação de similaridade",
        message: "Não foi possível verificar se existem ideias similares. Deseja continuar mesmo assim?",
        color: "orange",
        autoClose: false,
        withCloseButton: true
      });
    }
  }

  return {
    form,
    isPending: isPending || isCheckingSimilarity,
    onSubmit: form.onSubmit(handleFormSubmit),
    submit: handleFormSubmit
  };
}

export default useIdeiaForm;
